/**
 * 应用外壳：登录 → 进教室 或 进管理后台。
 */

import { useCallback, useEffect, useState } from 'react'

import { wsURL } from './lib/endpoint'
import { hasPerm, Perm } from './lib/protocol'
import { Admin } from './pages/Admin'
import { Classroom } from './pages/Classroom'
import { Login, type LoginResult } from './pages/Login'
import { Prejoin, type PrejoinChoice } from './pages/Prejoin'
import { Waiting } from './pages/Waiting'
import { useRoomStore } from './stores/roomStore'

type View =
  | { kind: 'login' }
  // 登录成功后先到准备页确认设备，确认了才真正连房间。
  | { kind: 'prejoin'; session: LoginResult }
  // 开了等候室时先落在候场页，等主持人放行。
  | { kind: 'waiting'; session: LoginResult; choice: PrejoinChoice }
  | { kind: 'classroom'; session: LoginResult; choice: PrejoinChoice }
  | { kind: 'admin'; session: LoginResult }

export function App() {
  const [view, setView] = useState<View>({ kind: 'login' })
  const [error, setError] = useState('')
  const connect = useRoomStore((s) => s.connect)
  const join = useRoomStore((s) => s.join)
  const leave = useRoomStore((s) => s.leave)

  const [entering, setEntering] = useState(false)

  const onLogin = useCallback((r: LoginResult, target: 'classroom' | 'admin') => {
    setError('')
    setView(target === 'admin' ? { kind: 'admin', session: r } : { kind: 'prejoin', session: r })
  }, [])

  /** 在准备页点「进入教室」：这时才真正连服务器。 */
  const enterRoom = useCallback(
    async (r: LoginResult, choice: PrejoinChoice) => {
      setError('')
      setEntering(true)
      try {
        // ICE 配置由服务端在握手应答里下发，这里先给一份兜底的公共 STUN。
        useRoomStore.getState().setSelfAccount(r.account)
        await connect(wsURL, r.token, [{ urls: ['stun:stun.l.google.com:19302'] }])
        await join(r.roomId, r.nick, r.roomPassword)
        // 服务端可能把我们放进了等候室（join 的应答是 wait.joined 而不是 room.joined）。
        setView(
          useRoomStore.getState().waiting
            ? { kind: 'waiting', session: r, choice }
            : { kind: 'classroom', session: r, choice },
        )
      } catch (e) {
        setError(e instanceof Error ? e.message : '进入教室失败')
      } finally {
        setEntering(false)
      }
    },
    [connect, join],
  )

  const exit = useCallback(() => {
    leave()
    setView({ kind: 'login' })
  }, [leave])

  // 主持人放行：客户端收到 wait.admitted 后重发 room.join 才算真进房。
  // 这一步必须由前端主动重来，服务端不能替他进 —— 席位是进房那一刻才分配的。
  const admitted = useRoomStore((s) => s.waiting?.admitted ?? false)
  useEffect(() => {
    if (!admitted || view.kind !== 'waiting') return
    const { session, choice } = view
    void (async () => {
      try {
        await join(session.roomId, session.nick, session.roomPassword)
        // 进房成功就不再是候场状态。不清的话下面那个「被移到等候室」的
        // effect 会看到 waiting 非空，立刻把人推回候场页 —— 表现为
        // 「准入了但进不去教室」。
        useRoomStore.getState().clearWaiting()
        setView({ kind: 'classroom', session, choice })
      } catch (e) {
        setError(e instanceof Error ? e.message : '进入教室失败')
      }
    })()
  }, [admitted, view, join])

  // 分组讨论的换房间：进小组、回主房间都是重新 join 一次。
  // 不能只切界面 —— 房间状态（白板、成员、麦序）全在服务端那一侧，
  // 必须真的换过去，否则小组里看到的还是主房间的板书。
  const switchRoom = useCallback(
    async (roomId: number, session: LoginResult, choice: PrejoinChoice) => {
      setError('')
      try {
        leave()
        await connect(wsURL, session.token, [{ urls: ['stun:stun.l.google.com:19302'] }])
        useRoomStore.getState().setSelfAccount(session.account)
        // 小组房间没有密码（服务端刻意不继承），只有回主房间才带密码。
        // 判据用 mainRoomId 而不是 session.roomId —— session.roomId 会被下面改成
        // 当前所在的房间号，用它判断的话「回主房间」会被当成「进小组」而不带密码。
        const isMain = roomId === (session.mainRoomId ?? session.roomId)
        await join(roomId, session.nick, isMain ? session.roomPassword : '')
        setView({
          kind: 'classroom',
          // mainRoomId 记住最初进的那间，供回主房间时判断与显示。
          session: { ...session, roomId, mainRoomId: session.mainRoomId ?? session.roomId },
          choice,
        })
      } catch (e) {
        setError(e instanceof Error ? e.message : '切换房间失败')
      }
    },
    [connect, join, leave],
  )

  // 主持人把已入场的人「移到等候室」时，服务端会补发一条 wait.joined。
  // 这时界面还停在教室页 —— 必须切回候场页，否则学员看到的是一个
  // 空教室（自己已被移出，别人都不见了），完全不知道发生了什么。
  const waitingNow = useRoomStore((s) => s.waiting)
  useEffect(() => {
    if (!waitingNow || view.kind !== 'classroom') return
    setView({ kind: 'waiting', session: view.session, choice: view.choice })
  }, [waitingNow, view])

  // 被拒绝：退回登录页并把原因显示出来。
  const rejected = useRoomStore((s) => s.waitRejected)
  useEffect(() => {
    if (!rejected) return
    setError(rejected)
    setView({ kind: 'login' })
  }, [rejected])

  switch (view.kind) {
    case 'prejoin':
      return (
        <>
          <Prejoin
            roomName={view.session.roomName || `教室 ${view.session.roomId}`}
            // 主持人不受入会静音影响：他进来就是要讲话的，被静音只会让他找半天开关。
            muteOnJoin={view.session.muteOnJoin && !hasPerm(view.session.prop, Perm.Op)}
            nick={view.session.nick || view.session.account}
            busy={entering}
            onEnter={(c) => void enterRoom(view.session, c)}
            onCancel={() => setView({ kind: 'login' })}
          />
          {error && <div className="toast global err">{error}</div>}
        </>
      )

    case 'waiting':
      return (
        <>
          <Waiting onCancel={exit} />
          {error && <div className="toast global err">{error}</div>}
        </>
      )

    case 'classroom':
      return (
        <>
          {/* 按房间号加 key：换房间（进小组 / 回主房间）时整个教室页重新挂载。
              不加的话工具栏不重挂，micOn 还是 true，但新的媒体引擎什么都没推 ——
              显示开着麦，别人听不到。重挂后会按入会选择重新开一次。 */}
          <Classroom
            key={view.session.roomId}
            session={view.session}
            startWith={view.choice}
            onExit={exit}
            onSwitchRoom={(rid) => void switchRoom(rid, view.session, view.choice)}
          />
          {error && <div className="toast global err">{error}</div>}
        </>
      )

    case 'admin':
      return (
        <Admin
          token={view.session.token}
          account={view.session.nick || view.session.account}
          onExit={() => setView({ kind: 'login' })}
        />
      )

    default:
      return (
        <>
          <Login onDone={onLogin} />
          {error && <div className="toast global err">{error}</div>}
        </>
      )
  }
}
